import React, { useEffect, useMemo, useState } from "react";
import { TrackToggle, MediaDeviceMenu, useLayoutContext, useLocalParticipant } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Mic, MicOff, Phone, Play, RefreshCw, X } from "lucide-react";
import {
  persistAudioOutputDeviceId,
  playTestSpeakerSound,
  readStoredAudioOutputDeviceId,
} from "../utils/liveInterviewAudioOutput";

function getDeviceLabel(devices, deviceId, fallbackPrefix) {
  const id = String(deviceId || "").trim();
  if (!id) return fallbackPrefix;
  const found = devices.find((d) => String(d.deviceId) === id);
  if (found?.label) return found.label;
  return `${fallbackPrefix}${id.slice(0, 6)}`;
}

export default function LiveInterviewSettingsMenu() {
  const { widget } = useLayoutContext();
  const { isMicrophoneEnabled } = useLocalParticipant();
  const [activeAudioTab, setActiveAudioTab] = useState("computer");

  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);

  const [selectedMicId, setSelectedMicId] = useState("");
  const [selectedSpeakerId, setSelectedSpeakerId] = useState(() =>
    readStoredAudioOutputDeviceId()
  );

  const refreshDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioInputs(devices.filter((d) => d.kind === "audioinput"));
      setAudioOutputs(devices.filter((d) => d.kind === "audiooutput"));
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (cancelled) return;
        setAudioInputs(devices.filter((d) => d.kind === "audioinput"));
        setAudioOutputs(devices.filter((d) => d.kind === "audiooutput"));
      } catch {
        // If enumerateDevices fails, menus will still show a fallback.
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const micLabel = useMemo(
    () => getDeviceLabel(audioInputs, selectedMicId, "Default - Microphone "),
    [audioInputs, selectedMicId]
  );
  const speakerLabel = useMemo(
    () => getDeviceLabel(audioOutputs, selectedSpeakerId, "Default - Speakers "),
    [audioOutputs, selectedSpeakerId]
  );

  return (
    <div className="li-settings-menu">
      <div className="li-settings-content">
        <div className="li-settings-header">Settings</div>
        <button
          type="button"
          className="li-settings-close-btn"
          aria-label="Close settings"
          title="Close"
          onClick={() => widget.dispatch?.({ msg: "toggle_settings" })}
        >
          <X size={18} />
        </button>

        <div className="li-settings-tabs" role="tablist" aria-label="Audio tabs">
          <button
            type="button"
            className={`li-settings-tab ${activeAudioTab === "computer" ? "is-active" : ""}`}
            role="tab"
            aria-selected={activeAudioTab === "computer"}
            onClick={() => setActiveAudioTab("computer")}
          >
            Computer Audio
          </button>
          <button
            type="button"
            className={`li-settings-tab ${activeAudioTab === "phone" ? "is-active" : ""}`}
            role="tab"
            aria-selected={activeAudioTab === "phone"}
            onClick={() => setActiveAudioTab("phone")}
          >
            Phone Audio
          </button>
        </div>

        <div className="li-settings-section">
          <div className="li-settings-row">
            <div className="li-settings-row-label">
              {isMicrophoneEnabled ? (
                <Mic size={16} aria-hidden />
              ) : (
                <MicOff size={16} aria-hidden />
              )}{" "}
              Microphone
            </div>
            <div className="li-settings-row-control">
              <TrackToggle className="li-settings-toggle" source={Track.Source.Microphone} showIcon={false} />
              <button
                type="button"
                className="li-settings-refresh-btn"
                onClick={refreshDevices}
                aria-label="Refresh microphone devices"
                title="Refresh microphone devices"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          <div className="li-settings-device-field">
            <MediaDeviceMenu
              className="li-settings-device-btn"
              kind="audioinput"
              title="Microphone devices"
              aria-label="Microphone devices"
              requestPermissions
              initialSelection={selectedMicId || undefined}
              onActiveDeviceChange={(_kind, deviceId) => setSelectedMicId(deviceId)}
            >
              <span className="li-settings-device-value">{micLabel}</span>
            </MediaDeviceMenu>
          </div>

          <div className="li-settings-row">
            <div className="li-settings-row-label">Speaker</div>
            <div className="li-settings-row-control">
              <button
                type="button"
                className="li-settings-refresh-btn"
                onClick={refreshDevices}
                aria-label="Refresh speaker devices"
                title="Refresh speaker devices"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          <div className="li-settings-row">
            <div className="li-settings-row-control li-settings-row-control--wide">
              <MediaDeviceMenu
                className="li-settings-device-btn"
                kind="audiooutput"
                title="Speaker devices"
                aria-label="Speaker devices"
                requestPermissions
              initialSelection={selectedSpeakerId || undefined}
              onActiveDeviceChange={(_kind, deviceId) => {
                setSelectedSpeakerId(deviceId);
                persistAudioOutputDeviceId(deviceId);
              }}
              >
                <span className="li-settings-device-value">{speakerLabel}</span>
              </MediaDeviceMenu>
              <button
                type="button"
                className="li-settings-play-btn"
                onClick={() => {
                  void playTestSpeakerSound(selectedSpeakerId);
                }}
                aria-label="Play test tone"
              >
                <Play size={16} />
                <span>Play</span>
              </button>
            </div>
          </div>

          {activeAudioTab === "phone" ? (
            <div className="li-settings-phone-hint">
              <Phone size={16} aria-hidden /> Phone Audio uses the same browser audio devices in this build.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

